import Close from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { descendantsOf } from "../../program-access/access-model";
import { useProgramAccess } from "../../program-access/hooks/use-program-access";
import { useProgramOverview } from "../../program-landing/hooks/use-program-overview";
import { pathText } from "../../program-timeline/move-destinations";
import { useMoveProgram } from "../hooks/use-move-program";
import { freeTitle, moveAccess, programMoveTargets, titleTaken } from "../program-move";
import { MoveAccessSection } from "./move-access-section";
import { MoveDestinationStep, TOP } from "./move-destination-step";
import { MoveDetailsStep } from "./move-details-step";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { LevelChange } from "../hooks/use-move-program";
import type { ChosenLevels } from "./move-access-section";
import type { Destination } from "./move-destination-step";
import type { FC } from "react";

interface MoveProps {
  tree: ProgramTree;
  program: ProgramNode;
  onClose: () => void;
  /** After it moves, and after it moves back. */
  onMoved: () => void;
}

/** The dialog's content, so that the search and the choice start again each time it opens. */
const MoveProgramForm: FC<MoveProps & ReturnType<typeof useMoveProgram>> = (props) => {
  const { tree, program, onClose, onMoved, move, moving } = props;
  const [query, setQuery] = useState("");
  const [destination, setDestination] = useState<Destination | null>(null);
  // The title in the new place, once the choice is made, if the old one is taken there, and the
  // levels that the admin picks.
  const [check, setCheck] = useState<{
    title: string;
    renamed: boolean;
    chosen: ChosenLevels;
  } | null>(null);
  const { data: overviews } = useProgramOverview();
  const pendo = usePendo();
  const { data: accessData } = useProgramAccess();

  const folders = descendantsOf(tree, program.id).length;
  const targets = programMoveTargets(tree, program, query);
  const parentId = destination === TOP ? null : destination;
  const parent = parentId ? tree.byId.get(parentId) : undefined;
  // Only a choice that the search still shows can be picked.
  const chosen =
    destination === TOP ||
    targets.some(({ node, disabled }) => node.id === destination && !disabled);

  const title = check?.title.trim() ?? "";
  const taken = !title || titleTaken(tree, program, parentId, title);
  const access = accessData ? moveAccess(accessData, tree, program, parentId) : undefined;
  // Each level that the admin changed, with the level of today, to put back on an undo.
  const changes = [...(access?.listed ?? []), ...(access?.newcomers ?? [])].flatMap(
    ({ person, today }) => {
      const level = check?.chosen[person.id];
      return level && level !== today ? [{ person_id: person.id, level, today }] : [];
    },
  );
  const forward: LevelChange[] = changes.map(({ person_id, level }) => ({ person_id, level }));
  const backward: LevelChange[] = changes.map(({ person_id, today }) => ({
    person_id,
    level: today,
  }));

  const submit = async (): Promise<void> => {
    if (!(await move(program.id, title, parentId, forward))) return;
    if (parentId)
      pendo?.track("program-moved", {
        programId: program.id,
        fromParentId: program.parent_id,
        toParentId: parentId,
      });
    else
      pendo?.track("program-folder-promoted", {
        programId: program.id,
        fromParentId: program.parent_id,
      });
    if (forward.length)
      pendo?.track("program-move-access-changed", {
        programId: program.id,
        people: forward.length,
      });
    onMoved();
    onClose();
    // One toast for each program, so that a newer move or the undo replaces it.
    const id = `move-program-${program.id}`;
    const back = program.parent_id;
    const undo = async (): Promise<void> => {
      if (!(await move(program.id, program.title, back, backward, program.order))) return;
      pendo?.track("program-move-undone", { programId: program.id, parentId: back });
      onMoved();
      toast.success(
        `Moved “${program.title}” back to ${back ? pathText(tree, back) : "the top level"}.`,
        { id },
      );
    };
    toast.success(
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <span>
          {parent
            ? `Moved “${title}” into ${parent.title}.`
            : `“${title}” is now a top-level program.`}
        </span>
        <Button
          size="small"
          onClick={() => void undo().catch(console.error)}
          data-analytics-id="program-move-undo"
        >
          Undo
        </Button>
      </Stack>,
      { id, duration: 10_000 },
    );
  };

  let heading = `Move “${program.title}”`;
  if (check) {
    heading = parent
      ? `Move “${program.title}” into ${parent.title}`
      : `Make “${program.title}” a top-level program`;
  }

  return (
    <>
      <DialogTitle id="move-program-title" sx={{ pr: 7 }}>
        <Typography
          variant="overline"
          sx={{ display: "block", fontWeight: 700, lineHeight: "20px", color: "text.secondary" }}
        >
          {check ? "Step 2 of 2 · Check the details" : "Step 1 of 2 · Choose where it goes"}
        </Typography>
        {heading}
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disabled={moving}
        sx={{ position: "absolute", right: 8, top: 8 }}
        data-analytics-id="program-move-close"
      >
        <Close />
      </IconButton>

      <DialogContent dividers>
        {check ? (
          <Stack spacing={3}>
            <MoveDetailsStep
              tree={tree}
              program={program}
              parent={parent}
              folders={folders}
              communications={
                overviews
                  ? (overviews.find(({ id }) => id === program.id)?.communications ?? 0)
                  : undefined
              }
              name={check.title}
              renamed={check.renamed}
              taken={taken}
              onName={(name) => setCheck({ ...check, title: name })}
            />
            {access ? (
              <MoveAccessSection
                program={program}
                parent={parent}
                listed={access.listed}
                newcomers={access.newcomers}
                chosen={check.chosen}
                onChosen={(levels) => setCheck({ ...check, chosen: levels })}
              />
            ) : null}
          </Stack>
        ) : (
          <MoveDestinationStep
            program={program}
            folders={folders}
            targets={targets}
            query={query}
            onQuery={setQuery}
            destination={destination}
            onPick={setDestination}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3 }}>
        <Stack direction="row" spacing={0.75} sx={{ mr: "auto", alignItems: "center" }}>
          <LockOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Only admins can move programs and folders.
          </Typography>
        </Stack>
        {check ? (
          <Button
            onClick={() => setCheck(null)}
            disabled={moving}
            data-analytics-id="program-move-back"
          >
            Back
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={moving} data-analytics-id="program-move-cancel">
          Cancel
        </Button>
        {check ? (
          <Button
            variant="contained"
            loading={moving}
            disabled={taken}
            onClick={() => void submit().catch(console.error)}
            data-analytics-id="program-move-confirm"
          >
            {parent ? "Move" : "Make it a program"}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!chosen}
            onClick={() =>
              setCheck({
                title: freeTitle(tree, program, parentId),
                renamed: titleTaken(tree, program, parentId, program.title),
                chosen: {},
              })
            }
            data-analytics-id="program-move-continue"
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </>
  );
};

/**
 * Moves a program or folder into another program or folder, or to the top level, in two steps: where
 * it goes, then the details. Then it says where it went, with a button to move it back. Only an admin
 * can move one.
 */
export const MoveProgramDialog: FC<MoveProps & { open: boolean }> = ({
  open,
  onClose,
  ...props
}) => {
  const mover = useMoveProgram();
  const close = (): void => {
    if (!mover.moving) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby="move-program-title"
    >
      <MoveProgramForm {...props} {...mover} onClose={close} />
    </Dialog>
  );
};
